import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { create } from 'zustand';
import { useVehicle } from './api';
import { collapsedTwinIds } from './common';
import { useConfiguratorFeatures } from './features';
import { MotornetEngine, type OrChoice } from './engine';
import type {
	Catalog,
	ConfiguratorProgress,
	CustomizerSubsection,
	CustomizerTab,
	Domain,
	OptionId,
	Step,
	VehicleRef,
} from './domain';
import type { Validator } from './validate';
import type {
	MotornetAccessoriesResponse,
	MotornetVehicleKind,
	StockVehicle,
} from 'src/motornet/types';
import {
	buildPreview,
	diffConsequences,
	solveState,
	type Action,
	type OrGroup,
	type PendingVM,
} from './resolution';
import { useRestoreStore } from './savedConfigurations/restoreStore';

export type { CustomizerSubsection, CustomizerTab, Step } from './domain';
export type { PendingVM } from './resolution';

const defaultValidator: Validator = () => {
	throw new Error('Validator not bound — call useBindValidator()');
};

export type VehicleMode = 'new' | 'stock';

export interface ConfiguratorState {
	step: Step;
	makeCode: string | null;
	makeLabel: string | null;
	modelCode: string | null;
	modelLabel: string | null;
	vehicle: VehicleRef | null;
	subsection: CustomizerSubsection;
	tab: CustomizerTab;

	selectedOptionIds: OptionId[];
	committedPicks: OptionId[];
	choices: OrChoice[];
	domains: Map<OptionId, Domain>;
	pinnedPicks: OptionId[];
	forcedBy: Map<OptionId, OptionId[]>;
	blockedBy: Map<OptionId, OptionId[]>;

	validating: boolean;
	pending: PendingVM | null;
	confirmError: string[] | null;
	defaultsSeeded: boolean;

	validator: Validator;
	catalog: Catalog | null;
	engine: MotornetEngine | null;
	response: MotornetAccessoriesResponse | null;
	vehiclePrice: number;

	/**
	 * "pronta consegna": la riga scelta nella lista stock del passo "Motore &
	 * Allestimento". Quando è valorizzata il configuratore lavora su un'auto già
	 * costruita: gli optional sono fissi, prezzi e scheda tecnica arrivano
	 * interamente dallo stock. L'engine resta quello costruito per l'allestimento
	 * — serve al ritorno a "Nuovo" — ma non viene più interrogato: non c'è alcun
	 * vincolo da risolvere su un'auto già assemblata.
	 */
	stockVehicle: StockVehicle | null;

	/**
	 * Quale delle due liste è a schermo nel passo "Motore & Allestimento".
	 * In 'stock' l'allestimento eventualmente scelto resta impostato ma dormiente:
	 * nessuno lo legge, e tornando a 'new' l'utente lo ritrova.
	 */
	vehicleMode: VehicleMode;
	setVehicleMode: (mode: VehicleMode) => void;
	/** Deseleziona la riga restando nella lista stock. */
	clearStockVehicle: () => void;
	/** Commit della pronta consegna: al riepilogo, senza validazione. */
	confirmStockVehicle: () => void;
	selectStockVehicle: (vehicle: StockVehicle) => void;

	setMake: (code: string, label: string) => void;
	setModel: (code: string, label: string) => void;
	setVehicle: (ref: VehicleRef) => void;
	clearVehicle: () => void;
	setValidator: (fn: Validator) => void;
	setCatalog: (catalog: Catalog) => void;
	setEngine: (engine: MotornetEngine | null) => void;
	setSubsection: (s: CustomizerSubsection) => void;
	setTab: (t: CustomizerTab) => void;
	backToEngine: () => void;
	toggleOption: (id: OptionId) => Promise<void>;
	chooseAlternative: (group: OrGroup, chosenId: OptionId) => Promise<void>;
	cancelPending: () => void;
	confirmPending: () => void;
	clearSelections: () => Promise<void>;
	goToStep: (step: Step) => void;
	goToSummary: () => Promise<void>;
	backToCustomizer: () => void;
	dismissConfirmError: () => void;
	hydrate: (
		partial: Partial<
			Pick<
				ConfiguratorState,
				| 'step'
				| 'makeCode'
				| 'makeLabel'
				| 'modelCode'
				| 'modelLabel'
				| 'vehicle'
				| 'subsection'
				| 'tab'
				| 'choices'
			>
		> & { optionIds?: OptionId[] }
	) => void;
	reset: () => void;
}

const initial = {
	step: 'brand-selector' as Step,
	makeCode: null,
	makeLabel: null,
	modelCode: null,
	modelLabel: null,
	vehicle: null as VehicleRef | null,
	subsection: 'engine' as CustomizerSubsection,
	tab: 'colors' as CustomizerTab,
	selectedOptionIds: [] as OptionId[],
	committedPicks: [] as OptionId[],
	choices: [] as OrChoice[],
	domains: new Map<OptionId, Domain>(),
	pinnedPicks: [] as OptionId[],
	forcedBy: new Map<OptionId, OptionId[]>(),
	blockedBy: new Map<OptionId, OptionId[]>(),
	validating: false,
	pending: null as PendingVM | null,
	confirmError: null as string[] | null,
	defaultsSeeded: false,
	catalog: null as Catalog | null,
	engine: null as MotornetEngine | null,
	response: null as MotornetAccessoriesResponse | null,
	vehiclePrice: 0,
	stockVehicle: null as StockVehicle | null,
	vehicleMode: 'new' as VehicleMode,
};

const resetSelectionPatch = () => ({
	selectedOptionIds: [] as OptionId[],
	committedPicks: [] as OptionId[],
	choices: [] as OrChoice[],
	pending: null as PendingVM | null,
	confirmError: null as string[] | null,
	validating: false,
	defaultsSeeded: false,
});

/**
 * Un veicolo di stock appartiene a un modello preciso: cambiare marca o modello
 * lo invalida, e riporta la lista su "Nuovo".
 */
const clearStockPatch = () => ({
	stockVehicle: null as StockVehicle | null,
	vehicleMode: 'new' as VehicleMode,
});

const clearEnginePatch = () => ({
	engine: null as MotornetEngine | null,
	response: null as MotornetAccessoriesResponse | null,
	vehiclePrice: 0,
	domains: new Map<OptionId, Domain>(),
	pinnedPicks: [] as OptionId[],
	forcedBy: new Map<OptionId, OptionId[]>(),
	blockedBy: new Map<OptionId, OptionId[]>(),
});

async function solveView(engine: MotornetEngine): Promise<{
	domains: Map<OptionId, Domain>;
	pinnedPicks: OptionId[];
	forcedBy: Map<OptionId, OptionId[]>;
	blockedBy: Map<OptionId, OptionId[]>;
}> {
	const domains = await engine.validDomains();
	const state = await engine.state();
	const picked = new Set(state.picks);

	const pinnedPicks: OptionId[] = [];
	for (const p of state.picks) {
		if (!(await engine.canRemove(p))) pinnedPicks.push(p);
	}

	const forcedBy = new Map<OptionId, OptionId[]>();
	const blockedBy = new Map<OptionId, OptionId[]>();
	for (const [id, domain] of domains) {
		if (domain === 'forced' && !picked.has(id)) {
			const holders = await engine.forcedBy(id);
			if (holders.length) forcedBy.set(id, holders);
		} else if (domain === 'forbidden') {
			const blockers = await engine.blockedBy(id);
			if (blockers.length) blockedBy.set(id, blockers);
		}
	}

	return { domains, pinnedPicks, forcedBy, blockedBy };
}

type GetState = () => ConfiguratorState;
type SetState = (
	partial:
		| Partial<ConfiguratorState>
		| ((s: ConfiguratorState) => Partial<ConfiguratorState>)
) => void;

let opChain: Promise<void> = Promise.resolve();
function serialize(task: () => Promise<void>): Promise<void> {
	const next = opChain.then(task, task);
	opChain = next.catch(() => {});
	return next;
}

let previewEngine: MotornetEngine | null = null;
let pendingBasePicks: OptionId[] = [];

/**
 * Promuove un engine (di anteprima) a stato committato: legge la configurazione
 * risolta e aggiorna picks/domini/riepilogo. Usato sia dalla conferma esplicita
 * della modale sia dall'auto-apply delle scelte senza conseguenze.
 */
async function commitEngine(
	set: SetState,
	engine: MotornetEngine,
	choices: OrChoice[]
): Promise<void> {
	const state = await engine.state();
	set({
		engine,
		selectedOptionIds: state.configuration,
		committedPicks: state.picks,
		// choices may belong to chain companions, not only picks
		choices: choices.filter((c) => state.configuration.includes(c.accessoryId)),
		...(await solveView(engine)),
		pending: null,
		validating: false,
		defaultsSeeded: true,
	});
}

/**
 * Valida col backend (se c'è un veicolo) la configurazione di un engine e, se ok,
 * la promuove a stato committato. Usata sia dalla conferma esplicita della modale
 * sia dall'auto-apply delle scelte senza conseguenze: la validazione backend viene
 * eseguita in entrambi i casi. `guardCancel` copre il caso in cui l'utente annulli
 * la modale mentre la validazione è ancora in corso (non applicabile all'auto-apply,
 * che non ha modale).
 */
async function applyEngine(
	set: SetState,
	get: GetState,
	engine: MotornetEngine,
	choices: OrChoice[],
	guardCancel: boolean
): Promise<void> {
	const { vehicle, validator } = get();
	if (vehicle) {
		set({ validating: true });
		let res;
		try {
			res = await validator(
				vehicle.eurotaxCode,
				vehicle.vehicleKind,
				engine.wireIds(),
				vehicle.year,
				vehicle.month
			);
		} catch {
			previewEngine = null;
			set({
				validating: false,
				pending: null,
				confirmError: ['Validazione non disponibile. Riprova.'],
			});
			return;
		}
		if (guardCancel && !get().pending) {
			previewEngine = null;
			set({ validating: false });
			return;
		}
		if (res.errors.length > 0) {
			console.error('confirm-time validate disagreement', {
				sent: engine.wireIds(),
				errors: res.errors,
			});
			previewEngine = null;
			set({
				validating: false,
				pending: null,
				confirmError: res.errors.map((e) => e.message),
			});
			return;
		}
	}

	previewEngine = null;
	await commitEngine(set, engine, choices);
}

async function openAction(
	set: SetState,
	get: GetState,
	action: Action,
	basePicks: OptionId[],
	choices: OrChoice[],
	autoApply: boolean
): Promise<void> {
	const { engine, response, vehiclePrice } = get();
	if (!engine || !response) return;

	set({ validating: true });
	try {
		const committed = await solveState(engine);
		const preview = await buildPreview(
			response,
			vehiclePrice,
			basePicks,
			action,
			choices
		);
		const trial = await solveState(preview);

		const consequences = diffConsequences(committed, trial, action);
		const orGroups =
			action.kind === 'select' ? preview.orGroups(action.id) : [];

		const removed = new Set(consequences.autoRemoved.map((r) => r.id));
		const excluded =
			action.kind === 'select'
				? preview.excludes(action.id).filter((id) => !removed.has(id))
				: [];

		// Se l'anteprima non produrrebbe altro che la riga "La tua scelta"
		// (nessun optional auto-incluso/rimosso, nessun gruppo OR, nessuna
		// esclusione mostrata — filtrando i gemelli collassati come fa il dialog),
		// la modale sarebbe inutile: applichiamo la scelta direttamente.
		const twins = collapsedTwinIds(get().catalog);
		const showExcluded = useConfiguratorFeatures.getState().showExcludedSection;
		const trivial =
			!consequences.rejected &&
			!consequences.noop &&
			consequences.autoAdded.filter((a) => !twins.has(a.id)).length === 0 &&
			consequences.autoRemoved.filter((r) => !twins.has(r.id)).length === 0 &&
			orGroups.length === 0 &&
			!(showExcluded && excluded.filter((id) => !twins.has(id)).length > 0);

		if (autoApply && trivial) {
			// niente pending → nessuna modale, ma la validazione backend gira lo
			// stesso prima di committare (gli errori emergono nel modale di rifiuto)
			await applyEngine(set, get, preview, choices, false);
			return;
		}

		previewEngine = preview;
		pendingBasePicks = basePicks;

		set({
			pending: {
				action,
				consequences,
				orGroups,
				choices,
				// every OR group needs an explicit choice — never defaulted
				canConfirm:
					!consequences.rejected &&
					orGroups.every((g) => g.chosenId !== undefined),
				excluded,
			},
			validating: false,
		});
	} catch (e) {
		console.error('configurator preview failed', e);
		previewEngine = null;
		set({ validating: false });
	}
}

async function resyncEngine(
	engine: MotornetEngine,
	picks: OptionId[],
	choices: OrChoice[]
): Promise<void> {
	await engine.setChoices(choices);
	const current = (await engine.state()).picks;
	for (const id of current) if (!picks.includes(id)) await engine.deselect(id);
	for (const id of picks) await engine.select(id);
}

export const useConfiguratorState = create<ConfiguratorState>((set, get) => ({
	...initial,
	validator: defaultValidator,

	setMake: (makeCode, makeLabel) =>
		set({
			step: 'model-selector',
			makeCode,
			makeLabel,
			modelCode: null,
			modelLabel: null,
			vehicle: null,
			subsection: 'engine',
			tab: 'colors',
			...resetSelectionPatch(),
			...clearEnginePatch(),
			...clearStockPatch(),
		}),

	setModel: (modelCode, modelLabel) =>
		set({
			step: 'customizer',
			modelCode,
			modelLabel,
			vehicle: null,
			subsection: 'engine',
			tab: 'colors',
			...resetSelectionPatch(),
			...clearEnginePatch(),
			...clearStockPatch(),
		}),

	setVehicle: (vehicle) =>
		set({
			vehicle,
			...resetSelectionPatch(),
			...clearEnginePatch(),
		}),
	clearVehicle: () =>
		set({
			vehicle: null,
			...resetSelectionPatch(),
			...clearEnginePatch(),
		}),

	setVehicleMode: (vehicleMode) =>
		set(
			vehicleMode === 'new'
				? { vehicleMode, stockVehicle: null }
				: { vehicleMode }
		),

	clearStockVehicle: () => set({ stockVehicle: null }),

	/**
	 * Non passa da `goToSummary`: quello valida gli accessori contro il backend e
	 * senza engine esce subito — e se un allestimento dormiente ne avesse lasciato
	 * uno, validerebbe la configurazione sbagliata.
	 */
	confirmStockVehicle: () =>
		set((s) =>
			s.stockVehicle ? { step: 'summary' as Step, confirmError: null } : {}
		),

	/** Sceglie una pronta consegna. La conferma è un passo a parte. */
	selectStockVehicle: (vehicle) => set({ stockVehicle: vehicle }),

	setValidator: (validator) => set({ validator }),
	setCatalog: (catalog) => set({ catalog }),
	setEngine: (engine) => set({ engine }),

	setSubsection: (s) =>
		set(s === 'engine' ? { subsection: s, tab: 'colors' } : { subsection: s }),
	setTab: (t) => set({ tab: t }),
	backToEngine: () => {
		set({
			subsection: 'engine',
			tab: 'colors',
			...resetSelectionPatch(),
		});
		get().clearSelections();
	},

	toggleOption: async (id) =>
		serialize(async () => {
			const { engine, committedPicks, choices } = get();
			if (!engine) return;
			const action: Action = {
				kind: committedPicks.includes(id) ? 'deselect' : 'select',
				id,
			};
			await openAction(set, get, action, committedPicks, choices, true);
		}),

	chooseAlternative: async (group, chosenId) =>
		serialize(async () => {
			const { pending } = get();
			if (!pending || pending.action.kind !== 'select') return;
			// drop this group's previous choice AND any choice nested under it
			// (switching branch orphans the nested picks). Scoped to the owning
			// accessory: two accessories can offer the same candidate, and without
			// this each one's answer would erase the other's.
			const groupIds = new Set(group.allIds);
			const next: OrChoice[] = [
				...pending.choices.filter(
					(c) =>
						!(
							c.accessoryId === group.accessoryId &&
							c.kind === group.kind &&
							groupIds.has(c.chosenId)
						)
				),
				{ accessoryId: group.accessoryId, kind: group.kind, chosenId },
			];
			await openAction(set, get, pending.action, pendingBasePicks, next, false);
		}),

	confirmPending: () =>
		void serialize(async () => {
			const { pending } = get();
			if (!pending || !previewEngine) {
				set({ pending: null });
				return;
			}
			await applyEngine(set, get, previewEngine, pending.choices, true);
		}),

	cancelPending: () => {
		previewEngine = null;
		pendingBasePicks = [];
		set({ pending: null });
	},

	clearSelections: async () =>
		serialize(async () => {
			const { engine } = get();
			if (!engine) return;
			previewEngine = null;
			for (const id of (await engine.state()).picks) await engine.deselect(id);
			await engine.setChoices([]);
			const state = await engine.state();
			set({
				selectedOptionIds: state.configuration,
				committedPicks: state.picks,
				choices: [],
				...(await solveView(engine)),
				pending: null,
				defaultsSeeded: true,
			});
		}),

	goToStep: (step) => {
		if (step === 'brand-selector') useRestoreStore.getState().clear();
		set((state) => {
			if (step === 'brand-selector')
				return { ...initial, validator: state.validator };
			if (step === 'model-selector')
				return {
					step: 'model-selector',
					modelCode: null,
					modelLabel: null,
					vehicle: null,
					subsection: 'engine',
					tab: 'colors',
					...resetSelectionPatch(),
					...clearEnginePatch(),
					...clearStockPatch(),
				};
			// sull'identità, non sulle etichette: quelle sono cosmetiche
			if (!state.makeCode || !state.modelCode) return {};
			return { step };
		});
	},

	goToSummary: async () => {
		const { engine, vehicle, validator } = get();
		if (!engine || !vehicle) return;
		set({ validating: true, confirmError: null });
		let res;
		try {
			res = await validator(
				vehicle.eurotaxCode,
				vehicle.vehicleKind,
				engine.wireIds(),
				vehicle.year,
				vehicle.month
			);
		} catch {
			set({
				validating: false,
				confirmError: ['Validazione non disponibile. Riprova.'],
			});
			return;
		}
		if (res.errors.length === 0) {
			set({ validating: false, confirmError: null, step: 'summary' as Step });
		} else {
			console.error('summary-time validate disagreement', {
				sent: engine.wireIds(),
				errors: res.errors,
			});
			set({
				validating: false,
				confirmError: res.errors.map((e) => e.message),
			});
		}
	},

	/**
	 * Dal riepilogo si torna dove ha senso tornare. In pronta consegna la scelta
	 * resta: l'utente rientra sulla lista stock con la riga ancora evidenziata,
	 * non deve ripeterla.
	 */
	backToCustomizer: () =>
		set((state) =>
			state.stockVehicle
				? {
						step: 'customizer' as Step,
						subsection: 'engine' as CustomizerSubsection,
						tab: 'colors' as CustomizerTab,
				  }
				: {
						step: 'customizer' as Step,
						subsection: 'exterior-interior' as CustomizerSubsection,
						tab: 'colors' as CustomizerTab,
				  }
		),

	dismissConfirmError: () => set({ confirmError: null }),

	hydrate: (partial) => {
		const { optionIds, ...nav } = partial;
		set((state) => ({ ...nav, step: nav.step ?? state.step }));
		if (!optionIds) return;
		void serialize(async () => {
			const { engine, choices } = get();
			if (!engine) {
				set({
					selectedOptionIds: optionIds,
					committedPicks: optionIds,
					defaultsSeeded: false,
				});
				return;
			}
			await resyncEngine(engine, optionIds, choices);
			const state = await engine.state();
			set({
				selectedOptionIds: state.configuration,
				committedPicks: state.picks,
				...(await solveView(engine)),
				defaultsSeeded: false,
			});
		});
	},

	reset: () => {
		useRestoreStore.getState().clear();
		set((state) => ({ ...initial, validator: state.validator }));
	},
}));

export function useVehicleSync() {
	const vehicle = useConfiguratorState((s) => s.vehicle);
	const { data } = useVehicle(vehicle);

	useEffect(() => {
		if (!data) return;
		useConfiguratorState.getState().setCatalog(data.catalog);
	}, [data]);

	useEffect(() => {
		if (!data) return;
		let cancelled = false;

		const vehiclePrice = data.vehicle.prices.retailPrice;
		void MotornetEngine.create(data.payload.accessories, vehiclePrice)
			.then(async (engine) => {
				if (cancelled) return;
				const state = useConfiguratorState.getState();
				useConfiguratorState.setState({
					engine,
					response: data.payload.accessories,
					vehiclePrice,
				});

				const saved = state.committedPicks;
				if (saved.length > 0) await resyncEngine(engine, saved, state.choices);

				const solved = await engine.state();
				if (cancelled) return;
				useConfiguratorState.setState({
					selectedOptionIds: solved.configuration,
					committedPicks: solved.picks,
					...(await solveView(engine)),
					defaultsSeeded: true,
				});
			})
			.catch((err) => console.error('configurator engine build failed', err));

		return () => {
			cancelled = true;
		};
	}, [data]);
}

const KIND_CODE: Record<OrChoice['kind'], string> = {
	vincolo: 'v',
	inclusione: 'i',
	esclusione: 'e',
};
const CODE_KIND: Record<string, OrChoice['kind']> = {
	v: 'vincolo',
	i: 'inclusione',
	e: 'esclusione',
};

/**
 * Gli id Motornet sono interi positivi. Il controllo esplicito sullo zero non è
 * pedanteria: `Number('')` vale 0 ed è un intero, quindi un segmento vuoto
 * (`opts=1,,2`, una virgola finale) verrebbe altrimenti letto come l'id 0.
 */
const isOptionId = (n: number): boolean => Number.isInteger(n) && n > 0;

const encodeChoices = (choices: OrChoice[]): string =>
	[...choices]
		.sort(
			(a, b) => a.accessoryId - b.accessoryId || a.kind.localeCompare(b.kind)
		)
		.map((c) => `${c.accessoryId}.${KIND_CODE[c.kind]}.${c.chosenId}`)
		.join(',');

const decodeChoices = (raw: string): OrChoice[] => {
	const out: OrChoice[] = [];
	for (const part of raw.split(',')) {
		const [acc, code, chosen] = part.split('.');
		const kind = CODE_KIND[code];
		const accessoryId = Number(acc);
		const chosenId = Number(chosen);
		if (!kind || !isOptionId(accessoryId) || !isOptionId(chosenId)) continue;
		out.push({ accessoryId, kind, chosenId });
	}
	return out;
};

const STEPS: Step[] = [
	'brand-selector',
	'model-selector',
	'customizer',
	'summary',
];
const SUBSECTIONS: CustomizerSubsection[] = ['engine', 'exterior-interior'];
const TABS: CustomizerTab[] = ['colors', 'options', 'packs'];
const KINDS: MotornetVehicleKind[] = ['auto', 'vcom', 'moto'];

export function useUrlSync() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const hydrated = useRef(false);

	useEffect(() => {
		if (hydrated.current) return;
		hydrated.current = true;
		useConfiguratorState.getState().hydrate(parseUrl(searchParams));
	}, [searchParams]);

	useEffect(() => {
		return useConfiguratorState.subscribe((state) => {
			const next = serializeState(state).toString();
			const current = new URLSearchParams(window.location.search).toString();
			if (next !== current) navigate({ search: next }, { replace: true });
		});
	}, [navigate]);
}

/**
 * Posizione da salvare (URL e localStorage) per un dato stato.
 *
 * La scelta di stock è volutamente effimera: non finisce né nell'URL né nel
 * progresso salvato, perché ricostruirla richiederebbe una GET per id e potrebbe
 * comunque ripescare un'auto nel frattempo venduta. Ne consegue che il riepilogo
 * di una pronta consegna non è un punto ripristinabile: si salva il passo da cui
 * lo si raggiunge, cioè il selettore allestimenti.
 */
export function resumePoint(s: {
	step: Step;
	subsection: CustomizerSubsection;
	stockVehicle: StockVehicle | null;
}): { step: Step; subsection: CustomizerSubsection } {
	return s.stockVehicle
		? { step: 'customizer', subsection: 'engine' }
		: { step: s.step, subsection: s.subsection };
}

export function serializeProgress(p: ConfiguratorProgress): URLSearchParams {
	const params = new URLSearchParams();
	params.set('step', p.step);
	if (p.makeCode) params.set('makeCode', p.makeCode);
	if (p.makeLabel) params.set('make', p.makeLabel);
	if (p.modelCode) params.set('modelCode', p.modelCode);
	if (p.modelLabel) params.set('model', p.modelLabel);
	if (p.vehicle) {
		params.set('eurotax', p.vehicle.eurotaxCode);
		params.set('kind', p.vehicle.vehicleKind);
		params.set('year', String(p.vehicle.year));
		params.set('month', String(p.vehicle.month));
	}
	if (p.step === 'customizer') {
		params.set('section', p.subsection);
		if (p.subsection === 'exterior-interior') params.set('tab', p.tab);
	}
	const ids = [...p.selectedOptions].sort((a, b) => a - b);
	if (ids.length) params.set('opts', ids.join(','));
	if (p.choices.length) params.set('ch', encodeChoices(p.choices));
	return params;
}

export function serializeState(s: ConfiguratorState): URLSearchParams {
	const at = resumePoint(s);
	return serializeProgress({
		step: at.step,
		makeCode: s.makeCode,
		makeLabel: s.makeLabel,
		modelCode: s.modelCode,
		modelLabel: s.modelLabel,
		vehicle: s.vehicle,
		subsection: at.subsection,
		tab: s.tab,
		selectedOptions: s.committedPicks,
		choices: s.choices,
	});
}

export function parseUrl(
	qs: URLSearchParams
): Parameters<ConfiguratorState['hydrate']>[0] {
	const out: Parameters<ConfiguratorState['hydrate']>[0] = {};

	const step = qs.get('step') as Step | null;
	out.step = step && STEPS.includes(step) ? step : 'brand-selector';

	const makeCode = qs.get('makeCode');
	if (makeCode) out.makeCode = makeCode;

	const makeLabel = qs.get('make');
	if (makeLabel) out.makeLabel = makeLabel;

	const modelCode = qs.get('modelCode');
	if (modelCode) out.modelCode = modelCode;

	const modelLabel = qs.get('model');
	if (modelLabel) out.modelLabel = modelLabel;

	const eurotax = qs.get('eurotax');
	const kind = qs.get('kind') as MotornetVehicleKind | null;
	const year = Number(qs.get('year'));
	const month = Number(qs.get('month'));
	if (eurotax && kind && KINDS.includes(kind) && year && month) {
		out.vehicle = { eurotaxCode: eurotax, vehicleKind: kind, year, month };
	}

	const section = qs.get('section') as CustomizerSubsection | null;
	if (section && SUBSECTIONS.includes(section)) out.subsection = section;

	const tab = qs.get('tab') as CustomizerTab | null;
	if (tab && TABS.includes(tab)) out.tab = tab;

	const ch = qs.get('ch');
	if (ch) out.choices = decodeChoices(ch);

	const opts = qs.get('opts');
	if (opts) {
		const ids: OptionId[] = [];
		for (const raw of opts.split(',')) {
			const n = Number(raw);
			if (isOptionId(n)) ids.push(n);
		}
		out.optionIds = ids;
	}

	return out;
}
